/**
 * 📝 «پیش‌نویس‌ها» — فهرست خطی فشرده + جزئیات در مودال.
 *
 * هر ردیف یک خط است (چه کسی، کجا، چه گفت، ما چه جواب دادیم) و هر چیز بیشتری —
 * گفتگو، تحلیل، مدل، هزینه، ویرایش و اکشن‌ها — داخل مودال باز می‌شود.
 * زبان بصری: کیت مشترک (`kit.tsx` / کلاس‌های `fac-*`).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, CircularProgress, TextField } from '@mui/material';
import { useSnackbar } from 'notistack';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DraftsRoundedIcon from '@mui/icons-material/DraftsRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { ResponsiveModal } from '../common';
import QueueDoctor from './QueueDoctor';
import userbotService, { UserbotDraft, UserbotTimelineRow } from '../../services/userbotService';
import {
  Bubble, Cell, EmptyHint, INTENT_FA, IconAction, Insights, LocalChips, Loading, Pill, Row, Rows,
  SectionCard, STAGE_FA, SubHead, Tag, UB, errMsg, fa, faDate, hueOf, initial, relTime, toman,
} from './kit';
import type { Tone } from './kit';

type StageFilter = 'pending' | 'waiting' | 'sent' | 'failed' | 'history' | 'skipped' | 'all';

const FILTERS: Array<{ k: StageFilter; l: string; hint: string }> = [
  { k: 'pending', l: 'منتظر تأیید', hint: 'جواب‌هایی که تا تأیید نکنید فرستاده نمی‌شوند' },
  { k: 'waiting', l: 'منتظر سرویس', hint: 'هوش مصنوعی در دسترس نبوده؛ به‌محض سالم‌شدن ادامه می‌دهند' },
  { k: 'sent', l: 'ارسال‌شده', hint: 'جواب‌هایی که واقعاً در تلگرام فرستاده شدند' },
  { k: 'failed', l: 'مشکل‌دار', hint: 'شکست‌خورده یا منقضی‌شده — با دلیل دقیق' },
  { k: 'history', l: 'تاریخچه', hint: 'هر چیزی که فرستاده، رد یا منقضی شده' },
  { k: 'skipped', l: 'ردشده توسط مدل', hint: 'دروازه‌بان گفت ارزش جواب‌دادن ندارد' },
  { k: 'all', l: 'همه', hint: 'بدون فیلتر' },
];

const stageOf = (k: string) => STAGE_FA[k] || { label: k, color: UB.muted };
const canAct = (stage: string) => ['pending', 'failed', 'expired'].includes(stage);
const COLS = '42px minmax(170px, 1.3fr) minmax(240px, 2.2fr) minmax(120px, 0.9fr) 128px 34px';

// ────────────────────────────── مودال جزئیات ──────────────────────────────

const DetailModal: React.FC<{
  draft: UserbotDraft;
  onClose: () => void;
  onDone: () => void;
}> = ({ draft, onClose, onDone }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [d, setD] = useState<UserbotDraft>(draft);
  const [text, setText] = useState(draft.final?.text ?? draft.draft?.text ?? '');
  const [busy, setBusy] = useState(false);
  const [hintOpen, setHintOpen] = useState(false);
  const [hint, setHint] = useState('');
  useEffect(() => {
    setD(draft);
    setText(draft.final?.text ?? draft.draft?.text ?? '');
  }, [draft]);

  const editable = canAct(d.stage);
  const st = stageOf(d.stage);

  const act = async (fn: () => Promise<string>) => {
    setBusy(true);
    try {
      enqueueSnackbar(await fn(), { variant: 'success' });
      onDone();
      onClose();
    } catch (e) {
      enqueueSnackbar(errMsg(e), { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    setHintOpen(false);
    setBusy(true);
    try {
      const r = await userbotService.regenerate(d._id, hint);
      setD(r.draft);
      setText(r.draft.draft?.text || '');
      enqueueSnackbar(r.message, { variant: 'success' });
      onDone();
    } catch (e) {
      enqueueSnackbar(errMsg(e), { variant: 'error' });
    } finally {
      setBusy(false);
      setHint('');
    }
  };

  return (
    <>
      <ResponsiveModal
        open
        onClose={onClose}
        maxWidth="md"
        disableClose={busy}
        icon={<DraftsRoundedIcon />}
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {d.senderName || 'عضو'}
            <Pill label={st.label} color={st.color} />
          </span>
        }
        subtitle={`${d.kind === 'dm' ? 'پیام خصوصی (دایرکت)' : d.chatTitle || d.chatId} · ${faDate(d.createdAt)}`}
        actions={
          editable ? (
            <>
              <Button size="small" color="inherit" disabled={busy} onClick={onClose}>بستن</Button>
              <Button size="small" color="error" startIcon={<CloseIcon />} disabled={busy} onClick={() => act(() => userbotService.reject(d._id))}>
                رد
              </Button>
              <Button size="small" startIcon={<AutorenewIcon />} disabled={busy} onClick={() => { setHint(''); setHintOpen(true); }}>
                بازنویسی
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={busy ? <CircularProgress size={14} /> : <SendIcon />}
                disabled={busy || !text.trim()}
                onClick={() => act(() => userbotService.approve(d._id, text))}
              >
                تأیید و ارسال
              </Button>
            </>
          ) : (
            <Button size="small" onClick={onClose}>بستن</Button>
          )
        }
      >
        {d.final?.error && <Alert severity={d.stage === 'approved' ? 'warning' : 'error'} sx={{ mb: 1.5 }}>{d.final.error}</Alert>}

        <SubHead hint="چه چیزی از پیام فهمیده شد و چرا وارد خط لوله شد">تحلیل</SubHead>
        <LocalChips local={d.local} />
        {d.gate && (
          <div className="fac-insight info" style={{ marginTop: 8 }}>
            <div>
              <b>دروازه‌بان: {d.gate.decision === 'reply' ? 'جواب بده' : 'نادیده بگیر'}</b> · {INTENT_FA[d.gate.intent] || d.gate.intent} · اطمینان {fa(Math.round(d.gate.confidence * 100))}٪
              <div className="fac-line-note">{d.gate.reason}</div>
              <div className="fac-line-note" style={{ direction: 'ltr', textAlign: 'right' }}>{d.gate.model} · {fa(d.gate.ms)}ms</div>
            </div>
          </div>
        )}

        <SubHead hint="پیام هدف با رنگ متفاوت مشخص شده">گفتگو</SubHead>
        <div className="ubc-scroll" style={{ background: 'var(--color-bg-default)', borderRadius: 12, padding: 10, maxHeight: 300, minHeight: 0 }}>
          {d.context.map((c, i) => (
            <Bubble key={i} ours={c.isOurs} who={c.isOurs ? 'ما' : c.senderName || 'عضو'} text={c.text} />
          ))}
          <Bubble
            highlight
            who={`${d.senderName}${d.senderUsername ? ` · @${d.senderUsername}` : ''}`}
            text={d.text}
            foot={d.isFollowUp ? <Tag color="primary" label="پیگیری روی پیام ما" /> : null}
          />
        </div>

        {!!d.products?.length && (
          <>
            <SubHead hint="از موجودی واقعی سایت — بدون لینک به مدل داده شد">محصول پیشنهادی</SubHead>
            <div className="fac-glossary">
              {d.products.map((p) => (
                <div key={p.name} className="fac-term" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <StorefrontIcon style={{ fontSize: 18, color: p.inStock === false ? 'var(--color-text-disabled)' : UB.ok }} />
                  <span style={{ minWidth: 0 }}>
                    <b>{p.name}</b>
                    <span>{p.price ? toman(p.price) : '—'}{p.inStock === false ? ' · ناموجود' : ''}</span>
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        <SubHead
          hint={
            <>
              {d.draft?.model ? <span style={{ direction: 'ltr', display: 'inline-block' }}>{d.draft.model} · {fa(d.draft.ms)}ms</span> : null}
              {` · هزینه ${fa(d.costUsd.toFixed(4))}$`}
              {d.sendAttempts ? ` · ${fa(d.sendAttempts)} تلاش ارسال` : ''}
            </>
          }
        >
          {editable ? 'جواب (قابل ویرایش)' : 'جواب'}
        </SubHead>
        {editable ? (
          <TextField value={text} onChange={(e) => setText(e.target.value)} fullWidth multiline minRows={3} />
        ) : (
          <Bubble ours who={d.stage === 'sent' ? 'ارسال شد' : 'جواب'} text={text || '—'} at={d.final?.sentAt} />
        )}
        {d.draft?.note && <p className="fac-note">🧠 {d.draft.note}</p>}
      </ResponsiveModal>

      <ResponsiveModal
        open={hintOpen}
        onClose={() => setHintOpen(false)}
        maxWidth="xs"
        title="بازنویسی پیش‌نویس"
        actions={
          <>
            <Button size="small" onClick={() => setHintOpen(false)}>انصراف</Button>
            <Button size="small" variant="contained" onClick={regenerate}>بازنویسی</Button>
          </>
        }
      >
        <TextField
          autoFocus
          label="راهنمای اختیاری برای مدل"
          value={hint}
          onChange={(e) => setHint(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          helperText="مثلاً: کوتاه‌تر؛ اسم سایت را نگو؛ اول بپرس نوع پوستش چیست"
        />
      </ResponsiveModal>
    </>
  );
};

// ────────────────────────────── تب ──────────────────────────────

/**
 * 📈 تایم‌لاین ساعت‌به‌ساعت — همان چیزی که پنل جوابش را نمی‌داد:
 * «کِی کار می‌کرده و کِی نه؟». هر ستون یک ساعت است؛ سبز = فرستاده‌شده،
 * قرمز = شکست/انقضا، زرد = منتظر سرویس، خاکستری = ردشدهٔ مدل. ساعتِ کاملاً خالی
 * یعنی هیچ پیامی از سرویس ربات نرسیده (سکوت = خوابیدن سرویس ربات).
 */
const Timeline: React.FC = () => {
  const [hours, setHours] = useState(48);
  const [rows, setRows] = useState<UserbotTimelineRow[]>([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await userbotService.draftsTimeline(hours);
      setRows(r.rows);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [hours]);
  useEffect(() => {
    load();
  }, [load]);
  const max = Math.max(1, ...rows.map((r) => r.received));
  const totals = rows.reduce(
    (a, r) => ({
      received: a.received + r.received,
      sent: a.sent + r.sent,
      failed: a.failed + r.failed,
      waiting: a.waiting + r.waiting,
      silent: a.silent + (r.received === 0 ? 1 : 0),
    }),
    { received: 0, sent: 0, failed: 0, waiting: 0, silent: 0 }
  );
  const label = (h: string) => `${h.slice(5).replace(' ', ' · ')}:00`;
  const pct = (n: number) => `${Math.round((n / max) * 100)}%`;
  return (
    <SectionCard
      icon={<AutorenewIcon />}
      title="کِی کار می‌کرده، کِی نه"
      hint="هر ستون یک ساعت (به وقت تهران). ساعت خالی یعنی هیچ پیامی از سرویس ربات نرسیده."
      actions={
        <>
          {[24, 48, 168].map((h) => (
            <Button key={h} size="small" variant={hours === h ? 'contained' : 'outlined'} onClick={() => setHours(h)}>
              {h === 168 ? 'یک هفته' : `${fa(h)} ساعت`}
            </Button>
          ))}
          <IconAction title="به‌روزرسانی" onClick={load}>
            <RefreshIcon />
          </IconAction>
        </>
      }
    >
      {loading && rows.length === 0 ? (
        <Loading />
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            <Tag label={`${fa(totals.received)} پیام رسیده`} />
            <Pill label={`${fa(totals.sent)} فرستاده‌شده`} color={UB.ok} />
            <Pill label={`${fa(totals.failed)} مشکل‌دار`} color={UB.bad} />
            <Pill label={`${fa(totals.waiting)} منتظر سرویس`} color={UB.warn} />
            <Tag label={`${fa(totals.silent)} ساعتِ بی‌پیام از ${fa(rows.length)}`} />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 120, direction: 'ltr', overflowX: 'auto', paddingBottom: 4 }}>
            {rows.map((r) => {
              const title =
                `${label(r.hour)} — رسیده ${fa(r.received)} · فرستاده ${fa(r.sent)} · مشکل‌دار ${fa(r.failed)} · منتظر ${fa(r.waiting)} · ردشده ${fa(r.skipped)}` +
                (r.topReason ? ` — دلیل غالب: ${r.topReason}` : '');
              return (
                <div
                  key={r.hour}
                  title={title}
                  style={{
                    flex: '0 0 12px',
                    display: 'flex',
                    flexDirection: 'column-reverse',
                    height: '100%',
                    background: r.received === 0 ? 'rgba(255,255,255,0.04)' : 'transparent',
                    borderRadius: 3,
                  }}
                >
                  <div style={{ height: pct(r.sent), background: UB.ok }} />
                  <div style={{ height: pct(r.failed), background: UB.bad }} />
                  <div style={{ height: pct(r.waiting), background: UB.warn }} />
                  <div style={{ height: pct(r.skipped + r.pending + r.other), background: UB.muted, opacity: 0.5 }} />
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', direction: 'ltr', fontSize: 11, opacity: 0.7 }}>
            <span>{rows[0] ? label(rows[0].hour) : ''}</span>
            <span>{rows[rows.length - 1] ? label(rows[rows.length - 1].hour) : ''}</span>
          </div>
        </>
      )}
    </SectionCard>
  );
};

const DraftsTab: React.FC<{ onChanged: () => void }> = ({ onChanged }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [stage, setStage] = useState<StageFilter>('pending');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ items: UserbotDraft[]; totalPages: number; total: number }>({
    items: [],
    totalPages: 1,
    total: 0,
  });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<UserbotDraft | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await userbotService.drafts({ stage, page, limit: 25 });
      setData({ items: r.items, totalPages: r.totalPages, total: r.total });
      setOpen((cur) => (cur ? r.items.find((x) => x._id === cur._id) || cur : null));
    } catch (e) {
      enqueueSnackbar(errMsg(e), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [stage, page, enqueueSnackbar]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (stage !== 'pending') return undefined;
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [stage, load]);

  const active = FILTERS.find((f) => f.k === stage)!;
  const insights = useMemo(() => {
    const out: Array<{ tone: Tone; text: React.ReactNode }> = [];
    if (loading) return out;
    if (stage === 'pending' && data.total > 0) {
      const oldest = data.items[data.items.length - 1];
      out.push({ tone: data.total > 20 ? 'warn' : 'info', text: <><b>{fa(data.total)}</b> جواب منتظر تأیید شماست{oldest ? <> و قدیمی‌ترینشان مربوط به {relTime(oldest.createdAt)} است</> : ''}. تا تأیید نکنید فرستاده نمی‌شوند و بعد از مدتی خودکار منقضی می‌شوند.</> });
    }
    if (stage === 'pending' && data.total === 0) out.push({ tone: 'good', text: 'هیچ جوابی منتظر تأیید نیست؛ صف تمیز است.' });
    if (stage === 'waiting' && data.total > 0) out.push({ tone: 'bad', text: <><b>{fa(data.total)}</b> پیام به‌خاطر در دسترس نبودن سرویس هوش مصنوعی متوقف شده‌اند. در تب تنظیمات بخش «جابه‌جایی سرویس» را ببینید؛ به‌محض سالم‌شدن خودکار ادامه می‌دهند.</> });
    if (stage === 'skipped' && data.total > 0) out.push({ tone: 'info', text: <>این‌ها پیام‌هایی‌اند که از فیلتر محلی گذشتند ولی دروازه‌بان گفت ارزش جواب‌دادن ندارند. اگر میان آن‌ها مشتری واقعی می‌بینید، آستانهٔ اطمینان را در تنظیمات پایین بیاورید.</> });
    return out;
  }, [stage, data, loading]);

  return (
    <>
      {/* وقتی صف پر است، اول باید بدانیم چرا — نه اینکه لیست را بالا و پایین کنیم */}
      <QueueDoctor onChanged={onChanged} />
      <Timeline />
      <Insights items={insights} />

      <SectionCard
        icon={<DraftsRoundedIcon />}
        title="پیش‌نویس‌ها"
        hint={`${active.hint} · روی هر ردیف بزنید تا گفتگو، تحلیل و اکشن‌هایش باز شود`}
        actions={
          <>
            {FILTERS.map((f) => (
              <Button
                key={f.k}
                size="small"
                variant={stage === f.k ? 'contained' : 'outlined'}
                onClick={() => { setStage(f.k); setPage(1); }}
              >
                {f.l}
              </Button>
            ))}
            <span className="fac-muted">{fa(data.total)} مورد</span>
            <IconAction title="به‌روزرسانی" onClick={load}><RefreshIcon /></IconAction>
          </>
        }
      >
        {loading && data.items.length === 0 ? (
          <Loading />
        ) : data.items.length === 0 ? (
          <EmptyHint
            icon={<DraftsRoundedIcon />}
            title={stage === 'pending' ? 'پیش‌نویسی منتظر تأیید نیست' : 'موردی نیست'}
            hint={stage === 'pending' ? 'در حالت خودکار، جواب‌ها بدون توقف ارسال می‌شوند و در «تاریخچه» دیده می‌شوند.' : undefined}
          />
        ) : (
          <Rows cols={COLS} minWidth={940}>
            <Row head>
              <span />
              <span>فرستنده و جا</span>
              <span>پیام و جواب</span>
              <span>تحلیل</span>
              <span>وضعیت</span>
              <span />
            </Row>
            {data.items.map((d) => {
              const st = stageOf(d.stage);
              const reply = d.final?.text || d.draft?.text || '';
              return (
                <Row key={d._id} onClick={() => setOpen(d)} title="کلیک: جزئیات کامل">
                  <span>
                    <span className="ubc-avatar sm" style={{ background: hueOf(d.senderId || d._id) }}>{initial(d.senderName)}</span>
                  </span>
                  <Cell top={d.senderName || 'عضو'} sub={d.kind === 'dm' ? 'پیام خصوصی' : d.chatTitle || d.chatId} />
                  <span className="fac-row-party">
                    <b style={{ fontWeight: 500 }}>{d.text}</b>
                    {reply && <small style={{ color: 'var(--color-primary)' }}>↩︎ {reply}</small>}
                  </span>
                  <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {d.kind === 'dm' && <Tag color="secondary" label="دایرکت" />}
                    {d.isFollowUp && d.kind !== 'dm' && <Tag color="primary" label="پیگیری" />}
                    {d.gate && <Tag label={INTENT_FA[d.gate.intent] || d.gate.intent} />}
                    {!!d.products?.length && <Tag color="success" label={`${fa(d.products.length)} محصول`} />}
                  </span>
                  <span>
                    <Pill label={st.label} color={st.color} />
                    <small className="fac-muted" style={{ display: 'block', marginTop: 3 }}>{relTime(d.createdAt)}</small>
                  </span>
                  <span className="fac-row-more"><ChevronLeftRoundedIcon style={{ fontSize: 18 }} /></span>
                </Row>
              );
            })}
          </Rows>
        )}

        {data.totalPages > 1 && (
          <div className="fac-pager" style={{ justifyContent: 'center' }}>
            <Button size="small" disabled={page <= 1} onClick={() => setPage(page - 1)}>قبلی</Button>
            <span>{fa(page)} / {fa(data.totalPages)}</span>
            <Button size="small" disabled={page >= data.totalPages} onClick={() => setPage(page + 1)}>بعدی</Button>
          </div>
        )}

        {open && (
          <DetailModal
            draft={open}
            onClose={() => setOpen(null)}
            onDone={() => { load(); onChanged(); }}
          />
        )}
      </SectionCard>
    </>
  );
};

export default DraftsTab;
